import React, { useEffect, useMemo, useState } from 'react'
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    getDay,
    startOfWeek
} from 'date-fns';
import ShiftEventsCalendar from './ShiftEventsCalendar';
import RequestChart from './RequestChart';
import ShiftRequestGridTable from './ShiftRequestGridTable';
import CustomAutocomplete from '../../../components/CustomAutocomplete';
import CustomSelect from '../../../components/CustomSelect';
import { ControlpanelPrimaryButton } from '../../../components/StyledButton';
import AddIcon from "@mui/icons-material/Add";
import AddShiftRequestDialog from './AddShiftRequestDialog';
import { useAuth } from '../../../context/AuthContext';
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LoadingProvider, useLoading } from '../../../context/LoadingContext';
import { format } from "date-fns";
import RosterApi from '../../../axios/Roster/RosterApi';
import { toast } from 'react-toastify';
import { getListUserGroup } from '../../../axios/Group/GroupUrlApi';
import { SignalWifiStatusbarNullOutlined } from '@mui/icons-material';

const ShiftRequest = ({ GroupData, shiftsData, usersData, allUser, handleRefreshData, refreshData }) => {
    const { t, i18n } = useTranslation();
    const { getRosterListAll } = RosterApi();
    const location = useLocation();
    const { myData } = location.state || {};
    const currentLang = i18n.language;
    const [dispalyWeek, setDispalyWeek] = useState(0);
    const [selectNewGroup, setSelectedNewGroup] = useState(1);
    const [selectNewShift, setSelectedNewShift] = useState(0);
    const [selectNewStatus, setSelectedNewStatus] = useState(0);
    const [openAddShiftEvent, setOpenAddShiftEvent] = useState(false);
    const { profile, isAdmin, isEmployee, genaralSettingDateFormat } = useAuth();
    const [showSummary, setShowSummary] = useState(true);

    // Tour: đảm bảo Summary (calendar/chart) đang mở
    useEffect(() => {
        window.__expandTourRequestSummary = () => {
            flushSync(() => setShowSummary(true));
        };
        return () => {
            delete window.__expandTourRequestSummary;
        };
    }, []);

    const [openEditShiftRequest, setOpenEditShiftRequest] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);

    const [openDetailShiftRequest, setOpenDetailShiftRequest] = useState(false);
    const [openDetailShiftRequestAR, setOpenDetailShiftRequestAR] = useState(false);
    const [show, hide] = useLoading();
    const [userGroup, setUserGroup] = useState([]);

    const shiftsWithAll = useMemo(() => {
        return [
            {
                shiftId: 0,
                shiftName: t("common.all-shifts"),
            },
            ...(shiftsData || [])
        ];
    }, [shiftsData, t]);

    const statusData = [
        {
            statusShiftRequestId: 0,
            statusShiftRequestName: t("common.all-status"),
        },
        {
            statusShiftRequestId: 1,
            statusShiftRequestName: t("common.pending"),
        },
        {
            statusShiftRequestId: 2,
            statusShiftRequestName: t("common.approved"),
        },
        {
            statusShiftRequestId: 3,
            statusShiftRequestName: t("common.rejected"),
        },
        {
            statusShiftRequestId: 4,
            statusShiftRequestName: t("common.partner-pending"),
        },
        {
            statusShiftRequestId: 5,
            statusShiftRequestName: t("common.partner-rejected"),
        },
    ];

    const handleOpenEdit = (row) => {
        setSelectedRequest(row);
        setOpenEditShiftRequest(true);
    };

    const handleCloseEdit = () => {
        setOpenEditShiftRequest(false);
        setSelectedRequest(null);
    };

    const handleOpenDetail = (row) => {
        setSelectedRequest(row);
        setOpenDetailShiftRequest(true);
    };

    const handleCloseDetail = () => {
        setOpenDetailShiftRequest(false);
        setSelectedRequest(null);
    };

    const handleOpenDetailAR = (row) => {
        setSelectedRequest(row);
        setOpenDetailShiftRequestAR(true);
    };

    const handleCloseDetailAR = () => {
        setOpenDetailShiftRequestAR(false);
        setSelectedRequest(null);
    };


    const handleOpenAddShiftEvent = () => {
        setOpenAddShiftEvent(true);
    };

    const handleCloseAddShiftEvent = () => {
        setOpenAddShiftEvent(false);
    };

    const [formData, setFormData] = useState({
        group: GroupData[0]?.id ? GroupData[0]?.id : 1,
        shift: shiftsData[0]?.id ? shiftsData[0]?.id : 0,
        statusShiftRequestId: 0,
        userId: "",
    });

    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(formData.group || null);
    const [selectedShift, setSelectedShift] = useState(formData.shift || null);
    const [selectedStatus, setSelectedStatus] = useState(formData.statusShiftRequestId || null);
    const [selectedUserId, setSelectedUserId] = useState(null);

    const [selectedDate, setSelectedDate] = useState(
        myData && !isNaN(new Date(myData).getTime()) ? new Date(myData) : new Date()
    );

    const calculateDisplayedWeeks = (date, weekStartsOnAbbr) => {
        let weekDayAbbrToIndex = {}
        if (currentLang === 'vi') {
            weekDayAbbrToIndex = {
                CN: 0,
                T2: 1,
                T3: 2,
                T4: 3,
                T5: 4,
                T6: 5,
                T7: 6,
            };
        } else {
            weekDayAbbrToIndex = {
                Sun: 0,
                Mon: 1,
                Tue: 2,
                Wed: 3,
                Thu: 4,
                Fri: 5,
                Sat: 6,
            };
        }

        const weekStartsOn = weekDayAbbrToIndex[weekStartsOnAbbr];
        if (weekStartsOn === undefined) {
            throw new Error("Invalid week start day. Use 'Sun', 'Mon', 'Tue', etc.");
        }

        const firstDayOfMonth = startOfMonth(date);
        const lastDayOfMonth = endOfMonth(date);

        // ngày đầu tuần thật sự chứa ngày 1 của tháng
        const calendarStartDate = startOfWeek(firstDayOfMonth, { weekStartsOn });

        // ngày cuối cùng trong tháng có thể tràn sang tuần sau
        const daysInGrid = eachDayOfInterval({
            start: calendarStartDate,
            end: lastDayOfMonth,
        });

        // cộng thêm ngày để đủ lưới 35 hoặc 42 ngày
        const totalCells = daysInGrid.length <= 35 ? 35 : 42;
        setDispalyWeek(totalCells / 7)
        return totalCells / 7;
    };

    useEffect(() => {
        if (selectedDate) {
            setFormData((prev) => ({
                ...prev,
                selectedDate: selectedDate
            }));
        }
    }, [selectedDate]);

    const handleDataChange = (name, newData) => {
        setFormData((prevData) => ({
            ...prevData,
            [name]: newData,
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        if (name === "group") {
            setSelectedGroup(value); // ✅ lưu groupId
            setSelectedNewGroup?.(value);
        }

        if (name === "shift") {
            setSelectedShift(value); // ✅ lưu shiftId
            setSelectedNewShift?.(value);
        }

        if (name === "statusShiftRequestId") {
            setSelectedStatus(value); // ✅ lưu statusShiftRequestId
            setSelectedNewStatus?.(value);
        }

    };

    useEffect(() => {
        const fetchData = async () => {
            const payload = {
                group: selectedGroup,
                pageNo: 1,
                recordPerPage: 50000,
                langId: localStorage.getItem("i18nextLngId")
            }
            show()
            try {
                const response = await getListUserGroup(payload);
                setUserGroup(response.data.data)

            } catch (error) {
                if (error.response) {
                    if (error.response.status !== 401) {
                        toast.error(t(error?.response.data.message));
                    }
                } else {
                    toast.error(t('common.network-timeout'));
                }
            } finally {
                hide()
            }
        };
        fetchData();

    }, [selectedGroup, t,]);

    return (
        <>
            <div className="flex flex-col gap-3 sm:gap-4 min-w-0 w-full">
                <div
                    className={`grid grid-cols-1 gap-3 min-w-0 ${isAdmin ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2"
                        }`}
                    data-tour="shift-and-roster-request-filter"
                >
                    {isAdmin && (
                        <CustomAutocomplete
                            className="w-full min-w-0"
                            name="user"
                            options={userGroup}
                            placeholder={t("common.content-161")}
                            dataType="user"
                            freeSolo={true}
                            value={selectedUser || null}
                            onDataChange={(_, value) => {
                                const nextUser = value || null;
                                setSelectedUser(nextUser);
                                handleDataChange("userId", nextUser?.userId || null);
                            }}
                        />
                    )}

                    {isAdmin && (
                        <CustomSelect
                            name="group"
                            id="group"
                            value={formData.group}
                            onChange={handleInputChange}
                            options={GroupData}
                            dataType="group"
                            className="w-full min-w-0"
                        />
                    )}

                    <CustomSelect
                        name="shift"
                        id="shift"
                        value={formData.shift}
                        onChange={handleInputChange}
                        options={shiftsWithAll}
                        dataType="shift"
                        className="w-full min-w-0"
                    />

                    <CustomSelect
                        name="statusShiftRequestId"
                        id="statusShiftRequestId"
                        value={formData.statusShiftRequestId}
                        onChange={handleInputChange}
                        options={statusData}
                        dataType="statusShiftRequestId"
                        className="w-full min-w-0"
                    />

                    {/* <ControlpanelPrimaryButton className='w-full md:max-w-[150px]' onClick={handleOpenAddShiftEvent}>
                        <AddIcon className="!text-[16px]" />
                        {t("common.add-requests")}
                    </ControlpanelPrimaryButton> */}
                </div>

                {/* Summary Toggle */}
                <div
                    className="flex items-center justify-between bg-white rounded-xl py-2 px-3 sm:px-4 cursor-pointer"
                    onClick={() => setShowSummary(prev => !prev)}
                >
                    <span className="font-semibold text-base sm:text-lg md:text-[20px]">
                        {t("common.summary")}
                    </span>

                    {showSummary ? (
                        <ExpandMoreIcon />
                    ) : (

                        <ExpandLessIcon />
                    )}
                </div>
                {/* 🔥 HÀNG TRÊN */}
                {showSummary && (
                    <div className="grid grid-cols-1 3xl:grid-cols-12 gap-3 sm:gap-4 min-w-0 px-0.5 sm:px-1">
                        {/* Calendar */}
                        <div
                            className="col-span-1 3xl:col-span-9 min-w-0"
                            data-tour="shift-and-roster-request-calendar"
                        >
                            <div className="card bg-white rounded-xl min-w-0 h-full">
                            <LoadingProvider><ShiftEventsCalendar
                                selectedDate={selectedDate}
                                setSelectedDate={setSelectedDate}
                                calculateDisplayedWeeks={calculateDisplayedWeeks}
                                selectedUser={selectedUser}
                                selectedGroup={selectedGroup}
                                selectedShift={selectedShift}
                                selectedStatus={selectedStatus}
                                refreshData={refreshData}
                            /></LoadingProvider>
                        </div></div>

                        <div
                            className="col-span-1 3xl:col-span-3 min-w-0"
                            data-tour="shift-and-roster-request-chart"
                        >
                            <div className="card bg-white rounded-xl flex justify-center items-center w-full min-w-0 h-full xl:ml-1">
                                <LoadingProvider><RequestChart selectedUser={selectedUser}
                                    selectedGroup={selectedGroup}
                                    selectedShift={selectedShift}
                                    selectedDate={selectedDate}
                                    selectedStatus={selectedStatus}
                                    userGroup={userGroup}
                                    refreshData={refreshData} /></LoadingProvider>

                            </div>
                        </div>
                    </div>
                )}

                {/* 🔥 HÀNG DƯỚI - TABLE */}
                <div
                    className="bg-white rounded-xl min-w-0 w-full overflow-x-auto"
                    data-tour="shift-and-roster-request-table"
                >
                    <LoadingProvider>
                        <ShiftRequestGridTable
                            selectedUser={selectedUser}
                            selectedGroup={selectedGroup}
                            selectedShift={selectedShift}
                            selectedDate={selectedDate}
                            selectedStatus={selectedStatus}
                            profileUserId={profile?.userId}
                            onEdit={handleOpenEdit}
                            onDetail={handleOpenDetail}
                            allUser={allUser}
                            shiftsData={shiftsData}
                            refreshData={refreshData}
                            handleRefreshData={handleRefreshData}
                            usersData={usersData}
                            GroupData={GroupData}
                        />
                    </LoadingProvider>

                </div>
            </div>

            {openAddShiftEvent && (
                <AddShiftRequestDialog
                    open={openAddShiftEvent}
                    onClose={handleCloseAddShiftEvent}
                    usersData={allUser}
                    shiftsData={shiftsData}
                    onRefresh={handleRefreshData}
                    selectedDate={selectedDate}
                />
            )}
        </>
    );
}

export default ShiftRequest